import type { ProjectVendorReviewTarget } from "@shared/types";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { StarRatingInput } from "@/components/ui/review-stars";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { VendorRating } from "@/components/ui/vendor-rating";
import { queryKeys } from "@/lib/query-keys";
import { saveProjectVendorReview } from "@/services/reviews-api.service";
import { useProjectVendorReviewEligibility } from "../hooks/use-project-vendor-review-eligibility";

/** Matches the longest comment the vendor rating endpoint accepts. */
const COMMENT_MAX_LENGTH = 2000;

interface VendorDraft {
	rating: number;
	comment: string;
	/** The user has edited this draft since it was last set from the server. */
	dirty: boolean;
}

/** DOM id of one vendor's block, so a BOQ line can scroll to it. */
export function vendorRatingAnchorId(vendorId: string): string {
	return `vendor-rating-${vendorId}`;
}

function toDraft(target: ProjectVendorReviewTarget): VendorDraft {
	return {
		rating: target.review?.rating ?? 0,
		comment: target.review?.comment ?? "",
		dirty: false,
	};
}

/**
 * Where the project's designer, homeowner or assigned contractor rates the
 * vendors the project used. Mount it only for those three: the endpoint refuses
 * everyone else.
 */
export function ProjectVendorRatingSection({
	projectId,
}: {
	projectId: string;
}): React.ReactElement {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { data, isLoading, isError, refetch } =
		useProjectVendorReviewEligibility(projectId, true);
	const [drafts, setDrafts] = useState<Record<string, VendorDraft>>({});
	const [savingId, setSavingId] = useState<string | null>(null);

	useEffect(() => {
		if (!data) return;
		// A draft follows the server until the user edits it. An edited draft is
		// kept, so saving one vendor (which refetches them all) does not discard
		// what is typed on another.
		setDrafts((current) => {
			const next: Record<string, VendorDraft> = {};
			for (const target of data.vendorTargets) {
				const existing = current[target.targetProfileId];
				next[target.targetProfileId] = existing?.dirty
					? existing
					: toDraft(target);
			}
			return next;
		});
	}, [data]);

	const handleSave = async (
		target: ProjectVendorReviewTarget,
	): Promise<void> => {
		const draft = drafts[target.targetProfileId];
		if (!draft || draft.rating < 1) {
			toast.error(t("vendorReview.chooseStars"));
			return;
		}

		setSavingId(target.targetProfileId);
		try {
			const result = await saveProjectVendorReview(projectId, {
				targetProfileId: target.targetProfileId,
				rating: draft.rating as 1 | 2 | 3 | 4 | 5,
				comment: draft.comment.trim() || undefined,
			});
			if (!result.success) {
				toast.error(result.error ?? t("vendorReview.saveFailed"));
				return;
			}
			toast.success(t("vendorReview.saved"));
			// Saved: the server value is now the source for this draft again.
			setDrafts((current) => ({
				...current,
				[target.targetProfileId]: { ...draft, dirty: false },
			}));
			// The shortlist's quote cards show the same rating.
			await Promise.all([
				refetch(),
				queryClient.invalidateQueries({
					queryKey: queryKeys.projects.shortlist(projectId),
				}),
			]);
		} finally {
			setSavingId(null);
		}
	};

	return (
		<Card>
			<CardHeader>
				<CardTitle>{t("vendorReview.title")}</CardTitle>
				<CardDescription>{t("vendorReview.description")}</CardDescription>
			</CardHeader>
			<CardContent className="space-y-4">
				{isLoading && (
					<>
						<Skeleton className="h-20 w-full" />
						<Skeleton className="h-20 w-full" />
					</>
				)}

				{isError && (
					<div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
						<span>{t("vendorReview.loadFailed")}</span>
						<Button variant="outline" size="sm" onClick={() => void refetch()}>
							{t("vendorReview.retry")}
						</Button>
					</div>
				)}

				{data && data.vendorTargets.length === 0 && (
					<p className="text-sm text-muted-foreground">
						{t("vendorReview.empty")}
					</p>
				)}

				{data?.vendorTargets.map((target) => {
					const draft = drafts[target.targetProfileId] ?? toDraft(target);
					const isSaving = savingId === target.targetProfileId;
					return (
						<div
							key={target.targetProfileId}
							id={vendorRatingAnchorId(target.targetProfileId)}
							className="space-y-3 rounded-md border p-4"
						>
							<div className="flex flex-wrap items-center justify-between gap-2">
								<span className="font-medium">{target.displayName}</span>
								<VendorRating
									rating={target.vendorRating}
									className="text-sm"
								/>
							</div>
							<StarRatingInput
								value={draft.rating}
								disabled={isSaving}
								onChange={(rating) =>
									setDrafts((current) => ({
										...current,
										[target.targetProfileId]: { ...draft, rating, dirty: true },
									}))
								}
							/>
							<div className="space-y-1">
								<Label htmlFor={`vendor-comment-${target.targetProfileId}`}>
									{t("vendorReview.commentLabel")}
								</Label>
								<Textarea
									id={`vendor-comment-${target.targetProfileId}`}
									value={draft.comment}
									maxLength={COMMENT_MAX_LENGTH}
									disabled={isSaving}
									placeholder={t("vendorReview.commentPlaceholder")}
									onChange={(event) =>
										setDrafts((current) => ({
											...current,
											[target.targetProfileId]: {
												...draft,
												comment: event.target.value,
												dirty: true,
											},
										}))
									}
								/>
							</div>
							<Button
								size="sm"
								disabled={isSaving}
								onClick={() => void handleSave(target)}
							>
								{target.review
									? t("vendorReview.update")
									: t("vendorReview.save")}
							</Button>
						</div>
					);
				})}
			</CardContent>
		</Card>
	);
}
